import { DashboardBarChartDiagramKpi, KpiDateRange, useKpiQuery } from './kpi';

export interface CarrierKpiMetric {
  value: number;
  progress: number;
}

export interface CarrierShipmentsSeries {
  yLabels: string[];
  values: number[];
}

export interface CarrierAnalyticsData {
  onTimeDelivery?: CarrierKpiMetric;
  firstAttempt?: CarrierKpiMetric;
  parcelShop?: CarrierKpiMetric;
  returnsNonDelivery?: CarrierKpiMetric;
  investigations?: CarrierKpiMetric;
  avgPackageWeight?: CarrierKpiMetric;
  shipmentsPerDay?: CarrierShipmentsSeries;
}

type MetricKey = Exclude<keyof CarrierAnalyticsData, 'shipmentsPerDay'>;

const VALUE_TITLES: Record<string, MetricKey> = {
  'On-Time Delivery': 'onTimeDelivery',
  'Share Delivered on First Attempt': 'firstAttempt',
  'Shares Landed in Parcel Shop': 'parcelShop',
  'Share of Returns Due to Non-Delivery': 'returnsNonDelivery',
  'Number of Investigations': 'investigations',
  'Average Package Weight': 'avgPackageWeight',
};

const PROGRESS_TITLES: Record<string, MetricKey> = {
  'On-Time Progress': 'onTimeDelivery',
  'First Attempt Progress': 'firstAttempt',
  'Parcel Shop Progress': 'parcelShop',
  'Returns Non-Delivery Progress': 'returnsNonDelivery',
  'Investigations Progress': 'investigations',
  'Average Package Weight Progress': 'avgPackageWeight',
};

export interface CarrierKpiApiItem {
  title: string;
  kpi_type: string;
  value?: number;
  yLabels?: string[];
  lines?: number[];
}

export const foldCarrierKpis = (items: CarrierKpiApiItem[]): CarrierAnalyticsData => {
  const values: Partial<Record<MetricKey, number>> = {};
  const progresses: Partial<Record<MetricKey, number>> = {};
  let shipmentsPerDay: CarrierShipmentsSeries | undefined;

  items.forEach(item => {
    if (item.kpi_type === 'dashboard_bar_chart_diagram') {
      shipmentsPerDay = { yLabels: item.yLabels ?? [], values: item.lines ?? [] };
      return;
    }
    if (item.kpi_type !== 'avg_calc' || typeof item.value !== 'number') {
      return;
    }
    const valueKey = VALUE_TITLES[item.title];
    if (valueKey) {
      values[valueKey] = item.value;
      return;
    }
    const progressKey = PROGRESS_TITLES[item.title];
    if (progressKey) {
      progresses[progressKey] = item.value;
    }
  });

  const analytics: CarrierAnalyticsData = { shipmentsPerDay };
  (Object.keys(values) as MetricKey[]).forEach(key => {
    analytics[key] = { value: values[key] ?? 0, progress: progresses[key] ?? 0 };
  });

  return analytics;
};

export interface CarrierKpiApiGroup {
  carrier_company_code?: string;
  carrier_company_name?: string;
  kpis?: CarrierKpiApiItem[];
}

export interface CarrierAnalyticsEntry {
  code: string;
  name: string;
  data: CarrierAnalyticsData;
}

export const groupCarrierKpis = (groups: CarrierKpiApiGroup[]): CarrierAnalyticsEntry[] =>
  groups
    .filter(group => group.carrier_company_code)
    .map(group => ({
      code: group.carrier_company_code as string,
      name: group.carrier_company_name || (group.carrier_company_code as string),
      data: foldCarrierKpis(group.kpis ?? []),
    }));

export interface CarrierKpiApiResponse {
  first_kpi_date?: string | null;
  last_kpi_date?: string | null;
  carriers?: CarrierKpiApiGroup[];
}

export const useCarrierAnalytics = (dateRange?: KpiDateRange) => {
  const { data, isLoading } = useKpiQuery('df.carrier.top.kpi', dateRange);
  const response = (data ?? {}) as CarrierKpiApiResponse;
  const groups = Array.isArray(response.carriers) ? response.carriers : [];
  return {
    carriers: groupCarrierKpis(groups),
    kpiDateBounds: {
      first: response.first_kpi_date ?? undefined,
      last: response.last_kpi_date ?? undefined,
    },
    isLoading,
  };
};

export type RangePreset =
  | 'yesterday'
  | 'last_7'
  | 'last_30'
  | 'month'
  | 'last_month'
  | 'months_3'
  | 'months_6'
  | 'months_12'
  | 'year'
  | 'total';

const isoDate = (value: Date): string => {
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${value.getFullYear()}-${month}-${day}`;
};

const addDays = (value: Date, days: number): Date => {
  const result = new Date(value);
  result.setDate(result.getDate() + days);
  return result;
};

export const rangeForPreset = (preset: RangePreset, today: Date, monthAnchor?: Date): KpiDateRange => {
  const yesterday = addDays(today, -1);
  switch (preset) {
    case 'yesterday':
      return { date_from: isoDate(yesterday), date_to: isoDate(yesterday) };
    case 'last_7':
      return { date_from: isoDate(addDays(yesterday, -6)), date_to: isoDate(yesterday) };
    case 'last_30':
      return { date_from: isoDate(addDays(yesterday, -29)), date_to: isoDate(yesterday) };
    case 'month': {
      const anchor = monthAnchor ?? today;
      const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
      const end = last < yesterday ? last : yesterday;
      return { date_from: isoDate(first), date_to: isoDate(end) };
    }
    case 'last_month': {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { date_from: isoDate(first), date_to: isoDate(last) };
    }
    case 'months_3':
      return { date_from: isoDate(addDays(yesterday, -89)), date_to: isoDate(yesterday) };
    case 'months_6':
      return { date_from: isoDate(addDays(yesterday, -179)), date_to: isoDate(yesterday) };
    case 'months_12':
      return { date_from: isoDate(addDays(yesterday, -364)), date_to: isoDate(yesterday) };
    case 'year':
      return {
        date_from: isoDate(new Date(today.getFullYear(), 0, 1)),
        date_to: isoDate(yesterday),
      };
    case 'total':
    default:
      return { date_to: isoDate(yesterday) };
  }
};

export interface KpiDateBounds {
  first?: string;
  last?: string;
}

export const presetIsVisible = (preset: RangePreset, today: Date, bounds?: KpiDateBounds): boolean => {
  if (!bounds?.first || !bounds.last || preset === 'total') {
    return true;
  }
  const { date_from: dateFrom } = rangeForPreset(preset, today);
  if (!dateFrom) {
    return true;
  }
  return dateFrom >= bounds.first && dateFrom <= bounds.last;
};

export const shareToPercent = (metric?: CarrierKpiMetric): number =>
  metric ? Math.round(metric.value * 1000) / 10 : 0;

export const progressToTrend = (metric?: CarrierKpiMetric): number => (metric ? Math.round(metric.progress * 100) : 0);

export type ChartGranularity = 'day' | 'week' | 'month';

const weekStart = (isoDate: string): string => {
  const date = new Date(`${isoDate}T00:00:00`);
  const daysSinceMonday = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - daysSinceMonday);
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

export const autoGranularity = (range: KpiDateRange, series?: CarrierShipmentsSeries): ChartGranularity => {
  let days = 0;
  const from = range.date_from ?? series?.yLabels[0];
  const to = range.date_to ?? series?.yLabels[series.yLabels.length - 1];
  if (from && to) {
    days = Math.round((Date.parse(to) - Date.parse(from)) / 86400000) + 1;
  }
  if (days <= 31) {
    return 'day';
  }
  if (days <= 126) {
    return 'week';
  }
  return 'month';
};

export const bucketShipmentsSeries = (
  series: CarrierShipmentsSeries | undefined,
  granularity: ChartGranularity,
): CarrierShipmentsSeries | undefined => {
  if (!series || granularity === 'day') {
    return series;
  }
  const buckets = new Map<string, number>();
  series.yLabels.forEach((label, index) => {
    const key = granularity === 'month' ? label.slice(0, 7) : weekStart(label);
    buckets.set(key, (buckets.get(key) ?? 0) + (series.values[index] ?? 0));
  });
  return {
    yLabels: Array.from(buckets.keys()),
    values: Array.from(buckets.values()),
  };
};

export const toShipmentsBarChartKpi = (
  series: CarrierShipmentsSeries | undefined,
  title: string,
  lineLabel: string,
): DashboardBarChartDiagramKpi | undefined => {
  if (!series || series.yLabels.length === 0) {
    return undefined;
  }
  return {
    kpi_type: 'dashboard_bar_chart_diagram',
    title,
    yLabels: series.yLabels,
    lines: [
      {
        label: lineLabel,
        data: series.values,
        color: '#36A9E1',
      },
    ],
  };
};
